import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { base_url } from '../api/index';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'motion/react';
import { User, Eye, EyeOff, Lock, Loader2, ArrowLeft, Delete, Users, RefreshCw, Sun, Moon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { usePermissions } from '../context/PermissionsContext';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n/I18nContext';
import {
  DeviceRevokedError,
  bootstrapDeviceToken,
  hasAppToken,
  isDeviceManaged,
  loadDeviceSession,
  reportDeviceRevoked,
} from '../api/pos/deviceSession';
import {
  clearStaffSession,
  hasWorkingSession,
  rememberStaffPermissions,
  recallStaffPermissions,
} from '../api/pos/session';

/**
 * POS terminalı rejimi: cihaz quraşdırma kodu ilə qeydiyyatdan keçib.
 *
 * Bu rejimdə giriş ekranının BİRİNCİ ADDIMI (restoran e-poçtu + şifrə) heç
 * vaxt göstərilmir və "Hesabı dəyiş" düyməsi də yoxdur — cihazın kimliyini
 * işçi yox, admin panel təyin edir. Kimliyi dəyişmək üçün yeganə yol paneldə
 * cihazı ləğv etməkdir; onda POS özü quraşdırma ekranına qayıdır.
 */
const DEVICE_MODE = isDeviceManaged();


const STAFF_COLORS = [
  'from-orange-500 to-red-500',
  'from-teal-500 to-cyan-500',
  'from-purple-500 to-pink-500',
  'from-green-500 to-emerald-500',
  'from-blue-500 to-indigo-500',
  'from-yellow-500 to-orange-500',
  'from-rose-500 to-red-500',
  'from-cyan-500 to-blue-500',
];

const STAFF_AVATARS = [
  'https://api.dicebear.com/8.x/initials/svg?seed=A&backgroundColor=f97316&textColor=ffffff&fontSize=42',
  'https://api.dicebear.com/8.x/initials/svg?seed=B&backgroundColor=14b8a6&textColor=ffffff&fontSize=42',
  'https://api.dicebear.com/8.x/initials/svg?seed=C&backgroundColor=a855f7&textColor=ffffff&fontSize=42',
  'https://api.dicebear.com/8.x/initials/svg?seed=D&backgroundColor=22c55e&textColor=ffffff&fontSize=42',
  'https://api.dicebear.com/8.x/initials/svg?seed=E&backgroundColor=3b82f6&textColor=ffffff&fontSize=42',
  'https://api.dicebear.com/8.x/initials/svg?seed=F&backgroundColor=eab308&textColor=ffffff&fontSize=42',
];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
};

// Rol çipi rəngləri — hər iki temada oxunaqlı kontrast
const ROLE_CHIP: Record<string, { dark: string; light: string }> = {
  manager: { dark: 'bg-orange-500/15 text-orange-300', light: 'bg-orange-100 text-orange-600' },
  owner: { dark: 'bg-amber-500/15 text-amber-300', light: 'bg-amber-100 text-amber-600' },
  cashier: { dark: 'bg-sky-500/15 text-sky-300', light: 'bg-sky-100 text-sky-600' },
  waiter: { dark: 'bg-emerald-500/15 text-emerald-300', light: 'bg-emerald-100 text-emerald-600' },
  courier: { dark: 'bg-violet-500/15 text-violet-300', light: 'bg-violet-100 text-violet-600' },
};

const roleChipClass = (role: string, isDark: boolean) => {
  const chip = ROLE_CHIP[String(role || '').toLowerCase()];
  if (chip) return isDark ? chip.dark : chip.light;
  return isDark ? 'bg-white/10 text-gray-400' : 'bg-gray-100 text-gray-500';
};

const ROLE_I18N_KEYS = ['waiter', 'cashier', 'manager', 'courier', 'owner'];

const Login = () => {
  const { loading: permissionsLoading, getDefaultRoute } = usePermissions();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useI18n();

  const roleLabel = (role) => {
    const r = String(role || '').toLowerCase();
    return ROLE_I18N_KEYS.includes(r) ? t(`login.role.${r}`) : role;
  };

  // Step: 'login' | 'staff-select' | 'pin-entry'
  // Cihaz rejimində 'login' addımı YOXDUR — dərhal işçi seçiminə keçilir.
  const [step, setStep] = useState(DEVICE_MODE ? 'staff-select' : 'login');
  /** Cihaz rejimində işçi siyahısı hələ gəlməyib (ilk yükləmə). */
  const [staffLoading, setStaffLoading] = useState(DEVICE_MODE);
  /** Siyahı yerli nüsxədən gəldi — internet yoxdur. */
  const [staffOffline, setStaffOffline] = useState(false);
  /** Yenilə düyməsi işləyir — siyahı ekranda QALIR, yalnız düymə fırlanır. */
  const [refreshing, setRefreshing] = useState(false);
  /**
   * Cihaz səviyyəsində problem: 'legacyCode' (kodda tətbiq tokeni yoxdur)
   * və ya 'revoked' (bulud tokeni qəbul etmədi). Konfiqurasiya AVTOMATİK
   * silinmir — ekran səbəbi göstərir, qərarı texniki işçi verir.
   */
  const [deviceError, setDeviceError] = useState<'legacyCode' | 'revoked' | null>(null);
  const [reconfiguring, setReconfiguring] = useState(false);

  // Step 1 - Restaurant Login
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Step 1 Response Data
  const [deviceToken, setDeviceToken] = useState('');
  const [restaurant, setRestaurant] = useState(null);
  const [staffList, setStaffList] = useState<any[]>([]);

  // Step 2 - Staff Selection
  const [selectedStaff, setSelectedStaff] = useState(null);

  // Step 3 - PIN Entry
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [isPinLoading, setIsPinLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Redirect
  const [redirectPath, setRedirectPath] = useState('');

  // Check existing token
  useEffect(() => {
    // DİQQƏT — burada `localStorage.token` YOXLANMIR.
    //
    // Əvvəl belə idi və dövrə yaradırdı: POS sessiyası bitəndə istifadəçi
    // bura yönləndirilirdi, bu effekt bulud token-ini görüb dərhal geri
    // qayıdırdı, POS yenə 401 verirdi. İşçi PIN ekranını heç vaxt görmürdü.
    //
    // İndi meyar İŞLƏK sessiyadır: bulud token-i VƏ pos_local sessiyası.
    if (hasWorkingSession()) {
      return;
    }

    // Yarımçıq sessiyanın qalıqlarını sil.
    //
    // Bulud token-i var, POS sessiyası yoxdursa istifadəçi "yarı girmiş"
    // vəziyyətdədir: PermissionsContext həmin token-lə icazə çəkməyə çalışır,
    // ekranda isə köhnə işçinin adı və rolu qalır. Bu, işçini çaşdırır —
    // sanki giriş edilib, amma heç nə işləmir.
    if (localStorage.getItem('token')) {
      clearStaffSession();
    }

    // ── POS terminalı: kimlik quraşdırma kodundan gəlir ─────────────────────
    if (DEVICE_MODE) {
      // ÖZ-ÖZÜNƏ KONFİQURASİYA SİLİNMİR.
      //
      // Əvvəl bu ekran problem görəndə cihazın konfiqurasiyasını silib
      // quraşdırma ekranını açırdı. Nəticə SONSUZ DÖVRƏ oldu: kod yazılır →
      // POS açılır → problem görünür → konfiqurasiya silinir → yenidən kod
      // ekranı. İşçi düzgün kod yazsa da cihaz heç vaxt qurulmurdu.
      //
      // Səbəb prinsipial idi: tək bir HTTP cavabına əsasən cihazın kimliyini
      // məhv etmək həddindən artıq sərt qərardır — 401 proxy-dən, səhv bulud
      // ünvanından və ya keçici nasazlıqdan da gələ bilər. İndi ekran yalnız
      // SƏBƏBİ göstərir, qərarı isə insan verir ("Yenidən qur" düyməsi).
      //
      // Avtomatik silmə YALNIZ pos_local-ın `authRevoked` bayrağında qalır:
      // orada səbəb buludun öz `AUTH_REVOKED` kodudur və hal davamlıdır.
      if (!hasAppToken()) {
        setDeviceError('legacyCode');
        setStaffLoading(false);
        return undefined;
      }

      const deviceTok = bootstrapDeviceToken();
      setDeviceToken(deviceTok);
      let cancelled = false;

      loadDeviceSession(deviceTok)
        .then((session) => {
          if (cancelled) return;
          setStaffList(session.staff);
          setRestaurant(session.restaurant);
          setStaffOffline(!session.online);
        })
        .catch((e) => {
          if (cancelled) return;
          if (e instanceof DeviceRevokedError) {
            setDeviceError('revoked');
            return;
          }
          setStaffOffline(true);
        })
        .finally(() => {
          if (!cancelled) setStaffLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }

    // ── Brauzer: köhnə 2 addımlı axın ───────────────────────────────────────
    const savedDeviceToken = localStorage.getItem('device_token');
    const savedStaff = localStorage.getItem('staff_list');
    const savedRestaurant = localStorage.getItem('restaurant_info');
    if (savedDeviceToken && savedStaff) {
      setDeviceToken(savedDeviceToken);
      setStaffList(JSON.parse(savedStaff));
      if (savedRestaurant) setRestaurant(JSON.parse(savedRestaurant));
      setStep('staff-select');
    }
    return undefined;
  }, []);

  useEffect(() => {
    // Yalnız HƏR İKİ token varsa avtomatik daxil ol. Əks halda işçi PIN
    // ekranını görməli və sessiyanı yeniləməlidir (bax: hasWorkingSession).
    if (!hasWorkingSession() || permissionsLoading) return;
    setRedirectPath(getDefaultRoute());
  }, [permissionsLoading, getDefaultRoute]);

  // Auto-submit PIN when 4 digits entered
  useEffect(() => {
    if (pin.length === 4 && selectedStaff && !isPinLoading) {
      handlePinLogin();
    }
  }, [pin]);

  const clearStorageData = () => {
    localStorage.removeItem("booked_table_color");
    localStorage.removeItem("empty_table_color");
    localStorage.removeItem("selectedCustomerId");
    localStorage.removeItem("urunType");
    localStorage.removeItem("masaType");
  };

  // Step 1: Restaurant Login
  const handleRestaurantLogin = async (e) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    setLoginError('');
    try {
      const response = await axios.post(`${base_url}/auth/restaurant-login`, {
        email,
        password,
        device_name: navigator.userAgent.substring(0, 50)
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      const { device_token, restaurant: rest, staff } = response.data;

      setDeviceToken(device_token);
      setRestaurant(rest);
      setStaffList(staff);

      // Save to localStorage for persistent device session
      localStorage.setItem('device_token', device_token);
      localStorage.setItem('staff_list', JSON.stringify(staff));
      if (rest) localStorage.setItem('restaurant_info', JSON.stringify(rest));

      setStep('staff-select');
    } catch (error: any) {
      if (error.response?.status === 401) {
        setLoginError(t('login.err.credentials'));
      } else if (error.response?.status === 403) {
        setLoginError(t('login.err.restaurantInactive'));
      } else {
        setLoginError(t('login.err.connection'));
        console.error('Restaurant login failed:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Select Staff
  const handleStaffSelect = (staff) => {
    setSelectedStaff(staff);
    setPin('');
    setPinError('');
    setStep('pin-entry');
  };

  // Step 3: PIN Login
  const handlePinLogin = async () => {
    if (pin.length !== 4) return;
    setIsPinLoading(true);
    setPinError('');
    try {
      const response = await axios.post(`${base_url}/auth/staff-login`, {
        staff_id: selectedStaff.id,
        pin: pin,
        device_token: deviceToken
      });

      const { access_token, staff: staffInfo } = response.data;
      localStorage.setItem('token', access_token);
      if (selectedStaff?.role) {
        const normalizedStaffRole = String(selectedStaff.role).trim().toLowerCase();
        localStorage.setItem('role', normalizedStaffRole);
        localStorage.setItem('staff_role', normalizedStaffRole);
      }
      // Effektiv icazələr (rol vasitəsilə) — PermissionsContext bunlarla nav/route
      // girişini müəyyən edir. Offline girişdə /me əlçatmaz olduğundan localStorage
      // son məlum mənbə kimi saxlanır. Custom rollar üçün rol adı yox, bu siyahı əsasdır.
      const permissionNames = Array.isArray(staffInfo?.all_permissions)
        ? staffInfo.all_permissions
        : Array.isArray(staffInfo?.permissions)
          ? staffInfo.permissions.map((p) => (typeof p === 'string' ? p : p?.name)).filter(Boolean)
          : [];
      // ── İCAZƏLƏR HƏMİŞƏ BU İŞÇİYƏ AİD OLMALIDIR ──
      //
      // Oflayn girişdə (pos_local fallback) cavabda `staff` yoxdur, yəni
      // siyahı boş gəlir. Əvvəlki kod belə halda `staff_permissions`-a
      // TOXUNMURDU — və orada ƏVVƏLKİ işçinin icazələri qalırdı. Menecer
      // çıxdıqdan sonra oflayn daxil olan ofisant onun səlahiyyətləri ilə
      // işləyirdi.
      //
      // İndi siyahı işçi id-si ilə keşlənir: onlayn girişdə yazılır, oflayn
      // girişdə həmin işçininki bərpa olunur.
      const staffId = selectedStaff?.id;
      if (permissionNames.length > 0) {
        localStorage.setItem('staff_permissions', JSON.stringify(permissionNames));
        rememberStaffPermissions(staffId, permissionNames);
      } else {
        localStorage.setItem(
          'staff_permissions',
          JSON.stringify(recallStaffPermissions(staffId)),
        );
      }
      clearStorageData();

      setIsLoggedIn(true);

      window.location.href = '/masalar';
    } catch (error: any) {
      setIsPinLoading(false);
      setPin('');
      if (error.response?.status === 401) {
        setPinError(t('login.err.wrongPin'));
      } else if (error.response?.status === 403) {
        setPinError(t('login.err.accountInactive'));
      } else if (error.response?.status === 404) {
        setPinError(t('login.err.staffNotFound'));
      } else if (error.response?.status === 503) {
        // POS sessiyası qurula bilmədi (işçi hələ bu cihaza sinxron olmayıb).
        // Ümumi "bağlantı xətası" yazmaq yanlış yönləndirərdi — internet
        // işləyir, sadəcə məlumat hələ gəlməyib.
        setPinError(error.response?.data?.message || t('login.err.notSyncedYet'));
      } else {
        // GERÇƏK SƏBƏBİ GÖSTƏR.
        //
        // Əvvəl hər gözlənilməz hal "Bağlantı xətası" yazırdı. Bu, ən pis
        // mesajdır: internet işləyəndə də çıxır və işçini səhv istiqamətə
        // yönəldir (modemi yoxlamağa). Nəticədə real səbəb — məsələn səhv
        // konfiqurasiya və ya server xətası — heç vaxt üzə çıxmır.
        //
        // Şəbəkə xətasında `error.response` ÜMUMİYYƏTLƏ olmur; yalnız o halda
        // bağlantı mesajı düzgündür.
        const serverMessage = error.response?.data?.message;
        const status = error.response?.status;
        setPinError(
          error.response
            ? `${serverMessage || t('login.err.connectionShort')}${status ? ` (${status})` : ''}`
            : t('login.err.connectionShort'),
        );
      }
    }
  };

  const handlePinInput = (digit) => {
    if (pin.length < 4 && !isPinLoading) {
      setPin(prev => prev + digit);
      setPinError('');
    }
  };

  const handlePinDelete = () => {
    if (!isPinLoading) {
      setPin(prev => prev.slice(0, -1));
      setPinError('');
    }
  };

  const handleBackToStaff = () => {
    setSelectedStaff(null);
    setPin('');
    setPinError('');
    setIsPinLoading(false);
    setStep('staff-select');
  };

  const handleRefreshStaff = async () => {
    // Cihaz rejimində yenidən e-poçt/şifrə ilə giriş etmək MÜMKÜN DEYİL —
    // onlar heç vaxt daxil edilməyib. Siyahı cihaz tokeni ilə yenilənir.
    if (DEVICE_MODE) {
      // `staffLoading` YOX, `refreshing` işlədilir: birincisi siyahını
      // spinner ilə əvəz edir və yeniləmə zamanı ekran boşalırdı. İşçi
      // siyahını görməyə davam etməlidir — yalnız düymə fırlanır.
      setRefreshing(true);
      try {
        const session = await loadDeviceSession(deviceToken);
        setStaffList(session.staff);
        setRestaurant(session.restaurant);
        setStaffOffline(!session.online);
      } catch (e) {
        if (e instanceof DeviceRevokedError) {
          setDeviceError('revoked');
          return;
        }
        setStaffOffline(true);
      } finally {
        setRefreshing(false);
      }
      return;
    }

    try {
      const response = await axios.post(`${base_url}/auth/restaurant-login`, {
        email,
        password,
        device_name: navigator.userAgent.substring(0, 50)
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      const { staff } = response.data;
      setStaffList(staff);
      localStorage.setItem('staff_list', JSON.stringify(staff));
    } catch (error: any) {
      // If refresh fails, just re-login
      handleLogoutDevice();
    }
  };

  const handleLogoutDevice = () => {
    localStorage.removeItem('device_token');
    localStorage.removeItem('staff_list');
    localStorage.removeItem('restaurant_info');
    localStorage.removeItem('role');
    localStorage.removeItem('staff_role');
    localStorage.removeItem('staff_permissions');
    setDeviceToken('');
    setStaffList([]);
    setRestaurant(null);
    setStep('login');
  };

  if (redirectPath) {
    return <Navigate to={redirectPath} />;
  }

  return (
    <>
      <Helmet>
        <title>{t('login.pageTitle')}</title>
        <meta name="description" content={t('login.pageDesc')} />
      </Helmet>

      <AnimatePresence mode="wait">
        {/* ==================== STEP 1: RESTAURANT LOGIN (Split-Screen) ==================== */}
        {step === 'login' && (
          <motion.div
            key="login-step"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.45 }}
            className={`min-h-screen flex overflow-hidden ${isDark ? 'bg-[#0d0e13]' : 'bg-white'
              }`}
          >
            {/* ========= LEFT PANEL — 4K Restaurant Image ========= */}
            <div className="hidden lg:flex relative w-[55%] flex-shrink-0 overflow-hidden">
              {/* Background image */}
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1556740738-b6a63e27c4df?auto=format&fit=crop&w=1920&q=80)' }}
              // style={{ backgroundImage: 'url(/pos_login_bg.png)' }}
              />
              {/* Dark gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/70" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />

              {/* Top-left badge */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="absolute top-8 left-8 z-10"
              >
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-4 py-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-white/90 text-xs font-bold tracking-wider uppercase">{t('login.livePos')}</span>
                </div>
              </motion.div>

              {/* Bottom content */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="absolute bottom-12 left-10 right-10 z-10"
              >
                <h1 className="text-4xl xl:text-5xl font-black text-white leading-tight mb-4">
                  {t('login.heroA')}<br />
                  <span className="bg-gradient-to-r from-[#ff8a00] to-[#ff6b35] bg-clip-text text-transparent">
                    {t('login.heroB')}
                  </span>{' '}{t('login.heroC')}
                </h1>
                <p className="text-white/70 text-base leading-relaxed max-w-md">
                  {t('login.heroText')}
                </p>

                <div className="flex items-center gap-6 mt-6">
                  {[
                    { num: '99.9%', label: t('login.stat.uptime') },
                    { num: '< 1s', label: t('login.stat.orderDelay') },
                    { num: '24/7', label: t('login.stat.support') },
                  ].map((stat) => (
                    <div key={stat.label}>
                      <div className="text-[#ff8a00] font-black text-xl">{stat.num}</div>
                      <div className="text-white/50 text-xs font-medium">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* ========= VERTICAL DIVIDER ========= */}
            <div className="hidden lg:block w-px bg-gradient-to-b from-transparent via-white/10 to-transparent flex-shrink-0" />

            {/* ========= RIGHT PANEL — Login Form ========= */}
            <div
              className={`flex-1 flex flex-col items-center justify-center px-8 sm:px-12 xl:px-16 relative ${isDark ? 'bg-[#0d0e13]' : 'bg-white'
                }`}
            >
              {/* Subtle glow */}
              <div className="absolute top-[-20%] right-[-20%] w-[60%] h-[60%] rounded-full bg-[#ff8a00]/5 blur-[120px] pointer-events-none" />
              <div className="absolute bottom-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-orange-600/5 blur-[100px] pointer-events-none" />

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="w-full max-w-sm relative z-10"
              >
                {/* Logo */}
                <div className="mb-8">
                  <div className="flex items-center gap-1">
                    <img
                      src={isDark ? '/rezmologodarkmode.png' : '/rezmologolightmode.png'}
                      alt="Rezmo"
                      className="h-44 w-auto object-contain -ml-5"
                      draggable={false}
                    />
                    {/* <p className="text-2xl font-black tracking-tight text-[#ff8a00] -ml-1">Rezmo POS</p> */}
                  </div>
                </div>

                {/* Heading */}
                <div className="mb-8">
                  <h2 className={`text-3xl font-black mb-2 tracking-tight ${isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                    {t('login.welcome')}
                  </h2>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                    {t('login.signInPrompt')}
                  </p>
                </div>

                <form onSubmit={handleRestaurantLogin} className="space-y-4">
                  {/* Email */}
                  <div className="space-y-1.5">
                    <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                      {t('login.emailLabel')}
                    </label>
                    <div className="relative">
                      <User className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'
                        }`} />
                      <Input
                        id="login-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('login.emailPh')}
                        required
                        autoComplete="email"
                        className={`pl-11 h-12 rounded-xl text-sm transition-all ${isDark
                          ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-[#ff8a00]/60 focus:bg-white/8'
                          : 'bg-gray-50 border-gray-200 focus:border-[#ff8a00] focus:bg-white'
                          }`}
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div className="space-y-1.5">
                    <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                      {t('login.password')}
                    </label>
                    <div className="relative">
                      <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'
                        }`} />
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        autoComplete="current-password"
                        className={`pl-11 pr-12 h-12 rounded-xl text-sm transition-all ${isDark
                          ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-[#ff8a00]/60 focus:bg-white/8'
                          : 'bg-gray-50 border-gray-200 focus:border-[#ff8a00] focus:bg-white'
                          }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                          }`}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Error */}
                  <AnimatePresence>
                    {loginError && (
                      <motion.div
                        initial={{ opacity: 0, y: -4, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className={`flex items-center gap-2 text-sm font-medium px-4 py-3 rounded-xl overflow-hidden ${isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'
                          }`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />
                        {loginError}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Submit */}
                  <Button
                    id="login-submit-btn"
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-12 bg-gradient-to-r from-[#ff8a00] to-[#ff6b35] hover:from-[#e87a00] hover:to-[#e55e2c] text-white rounded-xl shadow-lg shadow-orange-500/25 disabled:opacity-50 text-sm font-bold tracking-wide transition-all active:scale-[0.98] mt-2"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      t('login.signIn')
                    )}
                  </Button>
                </form>

                <div className="mt-5 text-center">
                  <button className={`text-xs font-medium transition-colors ${isDark ? 'text-gray-500 hover:text-[#ff8a00]' : 'text-gray-400 hover:text-[#ff8a00]'
                    }`}>
                    {t('login.forgotPassword')}
                  </button>
                </div>
              </motion.div>

              {/* Footer */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className={`absolute bottom-6 left-0 right-0 flex justify-between px-8 sm:px-12 xl:px-16 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'
                  }`}
              >
                <span>{t('login.copyright')}</span>
                <span className="font-bold">Rezmo v1.1</span>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/*
          ==================== CİHAZ XƏTASI ====================

          İşçi seçimini ƏVƏZ EDİR (onunla yanaşı deyil): siyahı onsuz da
          gətirilə bilmir, boş ekran göstərmək isə səbəbi gizlədərdi.

          Bu ekranın bütün mənası odur ki, texniki işçi restoranda nə etməli
          olduğunu bilsin. Ona görə mətn ümumi deyil, konkret addımdır.
        */}
        {DEVICE_MODE && deviceError && (
          <motion.div
            key="device-error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`min-h-screen flex flex-col items-center justify-center gap-6 p-8 text-center ${isDark ? 'bg-[#0d0e13]' : 'bg-[#f6f7f9]'
              }`}
          >
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-red-500/10">
              <Lock className="h-9 w-9 text-red-500" />
            </div>
            <div className="max-w-md space-y-2">
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t(deviceError === 'legacyCode' ? 'device.legacyCode' : 'device.revoked')}
              </h2>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                {t(deviceError === 'legacyCode' ? 'device.legacyCodeHint' : 'device.revokedHint')}
              </p>
            </div>
            <button
              onClick={async () => {
                setReconfiguring(true);
                await reportDeviceRevoked(
                  deviceError === 'legacyCode' ? 'köhnə quraşdırma kodu' : 'bulud tokeni qəbul etmədi',
                );
              }}
              disabled={reconfiguring}
              className="rounded-xl bg-[#ff8a00] px-8 py-3 text-sm font-bold uppercase tracking-wider text-white transition hover:brightness-110 disabled:opacity-60"
            >
              {reconfiguring ? t('device.reconfiguring') : t('device.reconfigure')}
            </button>
          </motion.div>
        )}

        {/* ==================== STEP 2: STAFF SELECTION ==================== */}
        {step === 'staff-select' && !deviceError && (
          <motion.div
            key="staff-step"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className={`min-h-screen flex flex-col items-center justify-center p-6 relative overflow-hidden ${isDark ? 'bg-[#0d0e13]' : 'bg-[#f6f7f9]'
              }`}
          >
            {/* Subtle brand glows */}
            <div className="absolute -top-[15%] -right-[10%] w-[45%] h-[45%] rounded-full bg-[#ff8a00]/[0.07] blur-[130px] pointer-events-none" />
            <div className={`absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full blur-[130px] pointer-events-none ${isDark ? 'bg-indigo-600/[0.08]' : 'bg-orange-300/15'
              }`} />

            {/*
              İşçi siyahısını yenilə.

              NİYƏ LAZIMDIR: paneldə yeni işçi əlavə olunanda o, siyahıda
              dərhal görünmür — sinxron gözləməli, ya da kiminsə girib-çıxması
              lazım gəlirdi. Quraşdırma zamanı texniki işçi məhz bunu edir.
              Bir düymə həmin dolanbacı aradan qaldırır.
            */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35 }}
              onClick={handleRefreshStaff}
              disabled={staffLoading}
              title={t('login.refresh')}
              className={`absolute top-6 right-[4.75rem] z-20 w-11 h-11 rounded-xl flex items-center justify-center border transition-colors disabled:opacity-50 ${isDark
                ? 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:bg-white/10'
                : 'bg-white border-gray-200 text-gray-500 hover:text-gray-900 shadow-sm hover:shadow'
                }`}
              aria-label={t('login.refresh')}
            >
              <RefreshCw className={`w-5 h-5 ${refreshing || staffLoading ? 'animate-spin' : ''}`} />
            </motion.button>

            {/* Theme toggle */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              onClick={toggleTheme}
              className={`absolute top-6 right-6 z-20 w-11 h-11 rounded-xl flex items-center justify-center border transition-colors ${isDark
                ? 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:bg-white/10'
                : 'bg-white border-gray-200 text-gray-500 hover:text-gray-900 shadow-sm hover:shadow'
                }`}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </motion.button>

            {/* Restaurant badge */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="relative z-10 mb-5"
            >
              <div className={`inline-flex items-center gap-3 backdrop-blur-md border rounded-2xl pl-2 pr-5 py-2 shadow-lg ${isDark
                ? 'bg-white/[0.06] border-white/10 shadow-black/30'
                : 'bg-white border-gray-200 shadow-gray-200/60'
                }`}>
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#ff8a00] to-[#ff6b35] flex items-center justify-center shadow-md shadow-orange-500/30">
                  <span className="text-white font-black text-base select-none">
                    {restaurant?.name ? String(restaurant.name).trim()[0].toUpperCase() : 'R'}
                  </span>
                </div>
                <span className={`font-bold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {restaurant?.name || 'Rezmo POS'}
                </span>
              </div>
            </motion.div>

            {/* Title + subtitle */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="relative z-10 text-center mb-12"
            >
              <h1 className={`text-4xl md:text-5xl font-black tracking-tight mb-3 ${isDark ? 'text-white' : 'text-gray-900'
                }`}>
                {t('login.whoIsLoggingIn')}
              </h1>
              <p className={`text-sm md:text-base ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                {t('login.selectProfileHint')}
              </p>
            </motion.div>

            {/* Staff Grid */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35 }}
              className="relative z-10 flex flex-wrap justify-center gap-5 md:gap-7 max-w-5xl"
            >
              {staffList.length > 0 ? (
                staffList.map((staff, index) => {
                  const colorClass = STAFF_COLORS[index % STAFF_COLORS.length];
                  const initials = getInitials(staff.name);

                  return (
                    <motion.button
                      key={staff.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.45 + index * 0.06 }}
                      whileHover={{ scale: 1.04, y: -4 }}
                      whileTap={{ scale: 0.96 }}
                      onClick={() => handleStaffSelect(staff)}
                      className={`group flex flex-col items-center gap-4 w-40 md:w-44 px-4 pt-5 pb-6 rounded-3xl border transition-all duration-300 ${isDark
                        ? 'bg-white/[0.04] border-white/[0.08] hover:border-[#ff8a00]/40 hover:bg-white/[0.07] hover:shadow-2xl hover:shadow-black/40'
                        : 'bg-white border-gray-200 shadow-sm hover:border-[#ff8a00]/50 hover:shadow-xl hover:shadow-orange-500/10'
                        }`}
                    >
                      <div className={`w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br ${colorClass} flex items-center justify-center shadow-lg relative overflow-hidden ring-4 ring-transparent group-hover:ring-[#ff8a00]/25 transition-all duration-300`}>
                        <span className="text-white text-3xl md:text-4xl font-bold select-none">
                          {initials}
                        </span>
                        <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-300" />
                      </div>
                      <div className="text-center min-w-0 w-full">
                        <p className={`font-bold text-sm md:text-base transition-colors truncate ${isDark
                          ? 'text-gray-200 group-hover:text-white'
                          : 'text-gray-800 group-hover:text-gray-900'
                          }`}>
                          {staff.name}
                        </p>
                        {staff.role && (
                          <span className={`inline-block mt-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide ${roleChipClass(staff.role, isDark)}`}>
                            {roleLabel(staff.role)}
                          </span>
                        )}
                      </div>
                    </motion.button>
                  );
                })
              ) : staffLoading ? (
                // Cihaz rejimində siyahı şəbəkədən gəlir — bir neçə saniyə çəkə
                // bilər. Bu aralıqda "işçi yoxdur" göstərmək YANLIŞDIR: işçi
                // sistemin boş olduğunu düşünüb quraşdırmanı pozmağa çalışar.
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center gap-4 py-16"
                >
                  <Loader2 className={`w-10 h-10 animate-spin ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <p className={isDark ? 'text-gray-500' : 'text-gray-500'}>{t('login.loadingStaff')}</p>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="flex flex-col items-center text-center py-8"
                >
                  <div className={`w-24 h-24 rounded-full border flex items-center justify-center mb-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200 shadow-sm'
                    }`}>
                    <Users className={`w-12 h-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {t('login.noStaff')}
                  </h3>
                  <p className={`max-w-sm mb-6 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    {t('login.noStaffText')}
                  </p>
                  <button
                    onClick={handleRefreshStaff}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl transition-colors border ${isDark
                      ? 'bg-white/10 hover:bg-white/15 text-white border-white/10'
                      : 'bg-white hover:bg-gray-50 text-gray-800 border-gray-200 shadow-sm'
                      }`}
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span className="text-sm font-medium">{t('login.refresh')}</span>
                  </button>
                </motion.div>
              )}
            </motion.div>

            {/*
              "Hesabı dəyiş" — YALNIZ brauzerdə.

              POS terminalında bu düymə səhv idi: onun yeganə işi cihaz
              tokenini silib e-poçt/şifrə ekranını açmaqdır, o ekran isə artıq
              yoxdur. İşçi ona basanda cihaz özünü işlək vəziyyətdən çıxarardı
              və restoranda düzəltmək mümkün olmazdı — kimlik yalnız admin
              panelin verdiyi quraşdırma kodu ilə bərpa olunur.
            */}
            {!DEVICE_MODE && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="relative z-10 mt-14"
              >
                <button
                  onClick={handleLogoutDevice}
                  className={`px-8 py-3 border rounded-xl transition-all duration-300 text-sm font-semibold tracking-wider uppercase ${isDark
                    ? 'border-white/15 text-gray-400 hover:text-white hover:border-white/40 hover:bg-white/5'
                    : 'border-gray-300 text-gray-500 hover:text-gray-900 hover:border-gray-400 hover:bg-white'
                    }`}
                >
                  {t('login.switchAccount')}
                </button>
              </motion.div>
            )}

            {/*
              Oflayn xəbərdarlığı: siyahı yerli nüsxədəndir.

              NİYƏ GÖRÜNMƏLİDİR: oflayn ilk dəfə giriş edən işçinin PIN-i yerli
              bazada yoxdur və giriş alınmayacaq. Səbəbini bilməsə işçi PIN-ini
              səhv saldığını düşünüb təkrar-təkrar cəhd edər.
            */}
            {DEVICE_MODE && staffOffline && (
              <div className="relative z-10 mt-10 flex items-center gap-2 rounded-full bg-amber-500/10 px-4 py-2 text-xs font-semibold text-amber-600 dark:text-amber-400">
                <span className="h-2 w-2 rounded-full bg-amber-500" />
                {t('login.offlineRoster')}
              </div>
            )}
          </motion.div>
        )}

        {/* ==================== STEP 3: PIN ENTRY ==================== */}
        {step === 'pin-entry' && selectedStaff && (
          <motion.div
            key="pin-step"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={`min-h-screen flex items-center justify-center p-4 relative overflow-hidden ${isDark ? 'bg-[#0d0e13]' : 'bg-[#f6f7f9]'
              }`}
          >
            {/* Subtle brand glows */}
            <div className="absolute -top-[15%] -right-[10%] w-[45%] h-[45%] rounded-full bg-[#ff8a00]/[0.07] blur-[130px] pointer-events-none" />
            <div className={`absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full blur-[130px] pointer-events-none ${isDark ? 'bg-indigo-600/[0.08]' : 'bg-orange-300/15'
              }`} />

            <AnimatePresence mode="wait">
              {!isLoggedIn ? (
                <motion.div
                  key="pin-form"
                  initial={{ opacity: 0, scale: 0.9, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 w-full max-w-sm"
                >
                  {/* Back Button */}
                  <motion.button
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    onClick={handleBackToStaff}
                    className={`absolute -top-16 left-0 flex items-center gap-2 transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                      }`}
                  >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="text-sm font-medium">{t('login.back')}</span>
                  </motion.button>

                  <div className={`backdrop-blur-2xl rounded-3xl border p-8 ${isDark
                    ? 'bg-white/[0.07] border-white/10 shadow-2xl'
                    : 'bg-white border-gray-200 shadow-2xl shadow-gray-300/50'
                    }`}>
                    {/* Avatar */}
                    <div className="flex flex-col items-center mb-8">
                      <motion.div
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 15 }}
                        className="relative mb-5"
                      >
                        {(() => {
                          const staffIndex = staffList.findIndex(s => s.id === selectedStaff.id);
                          const colorClass = STAFF_COLORS[staffIndex >= 0 ? staffIndex % STAFF_COLORS.length : 0];
                          const initials = getInitials(selectedStaff.name);
                          return (
                            <div className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${colorClass} flex items-center justify-center shadow-2xl border-2 border-white/20`}>
                              <span className="text-white text-3xl font-bold select-none">{initials}</span>
                            </div>
                          );
                        })()}
                        <div className={`absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-br from-[#ff8a00] to-[#ff6b35] rounded-lg border-2 flex items-center justify-center ${isDark ? 'border-[#15161c]' : 'border-white'
                          }`}>
                          <Lock className="w-4 h-4 text-white" />
                        </div>
                      </motion.div>

                      <motion.h2
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}
                      >
                        {selectedStaff.name}
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                      >
                        {roleLabel(selectedStaff.role) || t('login.staffFallback')} &bull; {t('login.enterPin')}
                      </motion.p>
                    </div>

                    {/* PIN Dots */}
                    <div className="flex justify-center gap-4 mb-8">
                      {[0, 1, 2, 3].map((i) => (
                        <motion.div
                          key={i}
                          animate={{
                            scale: pin.length === i ? 1.15 : 1,
                            borderColor: pin.length > i
                              ? '#ff8a00'
                              : pinError
                                ? 'rgba(239, 68, 68, 0.5)'
                                : isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
                          }}
                          className={`w-14 h-14 rounded-2xl border-2 flex items-center justify-center transition-all ${isDark ? 'bg-white/5' : 'bg-gray-50'
                            }`}
                        >
                          {pin.length > i && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-4 h-4 rounded-full bg-gradient-to-br from-[#ff8a00] to-[#ff6b35]"
                            />
                          )}
                        </motion.div>
                      ))}
                    </div>

                    {/* Error */}
                    <AnimatePresence>
                      {pinError && (
                        <motion.p
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          className={`text-sm text-center mb-4 font-medium ${isDark ? 'text-red-400' : 'text-red-500'}`}
                        >
                          {pinError}
                        </motion.p>
                      )}
                    </AnimatePresence>

                    {/* PIN Keypad */}
                    <div className="grid grid-cols-3 gap-3 mb-6">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                        <motion.button
                          key={digit}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.92 }}
                          onClick={() => handlePinInput(digit.toString())}
                          disabled={isPinLoading}
                          className={`h-16 rounded-2xl font-bold text-2xl transition-all border disabled:opacity-30 ${isDark
                            ? 'bg-white/[0.06] hover:bg-white/10 text-white border-white/5'
                            : 'bg-gray-50 hover:bg-gray-100 text-gray-900 border-gray-200'
                            }`}
                        >
                          {digit}
                        </motion.button>
                      ))}
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.92 }}
                        onClick={handlePinDelete}
                        disabled={isPinLoading}
                        className={`h-16 rounded-2xl font-bold transition-all border flex items-center justify-center disabled:opacity-30 ${isDark
                          ? 'bg-white/[0.06] hover:bg-red-500/20 text-gray-400 hover:text-red-400 border-white/5'
                          : 'bg-gray-50 hover:bg-red-50 text-gray-500 hover:text-red-500 border-gray-200'
                          }`}
                      >
                        <Delete className="w-6 h-6" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.92 }}
                        onClick={() => handlePinInput('0')}
                        disabled={isPinLoading}
                        className={`h-16 rounded-2xl font-bold text-2xl transition-all border disabled:opacity-30 ${isDark
                          ? 'bg-white/[0.06] hover:bg-white/10 text-white border-white/5'
                          : 'bg-gray-50 hover:bg-gray-100 text-gray-900 border-gray-200'
                          }`}
                      >
                        0
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.92 }}
                        onClick={() => { setPin(''); setPinError(''); }}
                        disabled={isPinLoading}
                        className={`h-16 rounded-2xl font-semibold text-xs transition-all border uppercase tracking-wider disabled:opacity-30 ${isDark
                          ? 'bg-white/[0.06] hover:bg-white/10 text-gray-400 hover:text-white border-white/5'
                          : 'bg-gray-50 hover:bg-gray-100 text-gray-500 hover:text-gray-900 border-gray-200'
                          }`}
                      >
                        {t('login.clear')}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ) : (
                /* ========= LOGGED IN - Loading Animation ========= */
                <motion.div
                  key="logged-in"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative z-10 flex flex-col items-center"
                >
                  {(() => {
                    const staffIndex = staffList.findIndex(s => s.id === selectedStaff.id);
                    const colorClass = STAFF_COLORS[staffIndex >= 0 ? staffIndex % STAFF_COLORS.length : 0];
                    const initials = getInitials(selectedStaff.name);
                    return (
                      <motion.div
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                        className={`w-36 h-36 rounded-3xl bg-gradient-to-br ${colorClass} flex items-center justify-center shadow-2xl border-4 border-white/20 mb-6`}
                      >
                        <span className="text-white text-5xl font-bold select-none">{initials}</span>
                      </motion.div>
                    );
                  })()}
                  <h2 className={`text-3xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedStaff.name}</h2>
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 text-[#ff8a00] animate-spin" />
                    <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t('login.loggingIn')}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Login;


